import { useState } from 'react';

const mainPlans = [
  {
    plan: 'Coverly Basico',
    includes: 'Titular + 2 familiares registrados',
    price: '$289',
    perDay: '$9.63 al dia',
    badge: 'Tarifa 2026',
    badgeStyle: 'main',
    highlight: false,
    features: ['Velacion en domicilio o funeraria', 'Ataud Nivel 1', 'Cafeteria estandar', 'Transporte hasta 100 km'],
  },
  {
    plan: 'Coverly Prime',
    includes: 'Titular + 3 familiares registrados',
    price: '$399',
    perDay: '$13.30 al dia',
    badge: 'Mejor opcion',
    badgeStyle: 'Prime',
    highlight: true,
    features: ['Todo lo del plan Basico', 'Ataud Nivel 2', 'Cremacion opcional incluida', 'Urna clasica o ecologica'],
  },
];

const extraPlans = [
  {
    plan: 'Basico + 1 familiar adicional',
    includes: 'Titular + 3 familiares registrados',
    price: '$369',
    perDay: '$12.30 al dia',
    badge: '',
    badgeStyle: '',
    highlight: false,
    features: ['Todo lo del plan Basico', '1 familiar adicional'],
  },
  {
    plan: 'Basico + cremacion',
    includes: 'Titular + 2 familiares registrados + cremacion',
    price: '$399',
    perDay: '$13.30 al dia',
    badge: '',
    badgeStyle: '',
    highlight: false,
    features: ['Todo lo del plan Basico', 'Cremacion opcional incluida'],
  },
];

type Plan = typeof mainPlans[0];

function TableRow({ row, idx, total }: { row: Plan; idx: number; total: number }) {
  return (
    <div
      className={`grid grid-cols-3 px-6 py-5 items-center transition-colors ${
        row.highlight ? 'bg-[#534AB7]' : idx % 2 === 0 ? 'bg-white' : 'bg-[#F7F6FF]'
      } ${idx < total - 1 ? 'border-b border-[#0A0A0A]/06' : ''}`}
    >
      <div className="flex flex-col gap-1 pr-2">
        <span className={`text-sm font-semibold ${row.highlight ? 'text-white' : 'text-[#0A0A0A]'}`}>
          {row.plan}
        </span>
        {row.badge && (
          <span
            className={`self-start text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${
              row.highlight ? 'bg-white text-[#534AB7]' : 'bg-[#534AB7]/10 text-[#534AB7]'
            }`}
          >
            {row.badge}
          </span>
        )}
      </div>

      <span className={`text-sm pr-2 ${row.highlight ? 'text-white/80' : 'text-[#0A0A0A]/55'}`}>
        {row.includes}
      </span>

      <div className="text-right">
        <span className={`font-title text-lg font-bold ${row.highlight ? 'text-white' : 'text-[#534AB7]'}`}>
          {row.price}/mes
        </span>
        <p className={`text-xs mt-0.5 ${row.highlight ? 'text-white/60' : 'text-[#0A0A0A]/40'}`}>
          {row.perDay}
        </p>
      </div>
    </div>
  );
}

function MobileCard({ plan }: { plan: Plan }) {
  return (
    <div
      className={`rounded-2xl p-5 flex flex-col gap-3 ${
        plan.highlight ? 'bg-[#534AB7]' : 'bg-white border border-[#0A0A0A]/08'
      }`}
      style={plan.highlight ? { boxShadow: '0 4px 24px rgba(83,74,183,0.25)' } : { boxShadow: '0 2px 12px rgba(10,10,10,0.06)' }}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className={`font-title text-base font-bold ${plan.highlight ? 'text-white' : 'text-[#0A0A0A]'}`}>
          {plan.plan}
        </span>
        {plan.badge && (
          <span
            className={`text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${
              plan.highlight ? 'bg-white text-[#534AB7]' : 'bg-[#534AB7]/10 text-[#534AB7]'
            }`}
          >
            {plan.badge}
          </span>
        )}
      </div>

      <div>
        <div className="flex items-end gap-1">
          <span className={`font-title text-4xl font-bold leading-none ${plan.highlight ? 'text-white' : 'text-[#534AB7]'}`}>
            {plan.price}
          </span>
          <span className={`text-sm mb-1 ${plan.highlight ? 'text-white/60' : 'text-[#0A0A0A]/50'}`}>/mes</span>
        </div>
        <p className={`text-xs mt-1 font-medium ${plan.highlight ? 'text-white/70' : 'text-[#534AB7]/70'}`}>
          {plan.perDay}
        </p>
      </div>

      <p className={`text-xs ${plan.highlight ? 'text-white/60' : 'text-[#0A0A0A]/50'}`}>
        {plan.includes}
      </p>

      <ul className="flex flex-col gap-1.5">
        {plan.features.map((f) => (
          <li key={f} className="flex items-center gap-2">
            <i className={`ri-checkbox-circle-fill text-base flex-shrink-0 ${plan.highlight ? 'text-white/80' : 'text-green-500'}`}></i>
            <span className={`text-xs leading-relaxed ${plan.highlight ? 'text-white/80' : 'text-[#0A0A0A]/65'}`}>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function PricingTable() {
  const [showExtra, setShowExtra] = useState(false);
  const allMain = mainPlans;
  const allExtra = extraPlans;

  return (
    <section className="bg-white py-14 md:py-20 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="font-title text-2xl md:text-3xl lg:text-4xl font-bold text-[#0A0A0A] mb-3">
            Cuanto pagarias segun tu familia?
          </h2>
          <p className="text-[#0A0A0A]/50 text-sm md:text-base max-w-xl mx-auto">
            Tarifas mensuales visibles desde el registro. Cualquier ajuste futuro se informa conforme a las condiciones del plan.
          </p>
        </div>

        <div className="flex flex-col gap-4 md:hidden">
          {allMain.map((plan) => (
            <MobileCard key={plan.plan} plan={plan} />
          ))}

          {showExtra && allExtra.map((plan) => (
            <MobileCard key={plan.plan} plan={plan} />
          ))}

          <div className="flex justify-center mt-2">
            <button
              onClick={() => setShowExtra((v) => !v)}
              className="inline-flex items-center justify-center gap-2 border border-[#534AB7]/30 text-[#534AB7] text-sm font-medium px-5 py-3 rounded-full hover:bg-[#534AB7]/5 transition-all duration-200 cursor-pointer whitespace-nowrap min-h-[48px]"
            >
              <i className={`${showExtra ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-base`}></i>
              {showExtra ? 'Ocultar opciones adicionales' : 'Ver mas opciones'}
            </button>
          </div>
        </div>

        <div className="hidden md:block overflow-x-auto rounded-2xl border border-[#0A0A0A]/08">
          <div className="min-w-[520px]">
            <div className="grid grid-cols-3 bg-[#0A0A0A] px-6 py-4">
              <span className="text-white/60 text-xs font-semibold tracking-wider uppercase">Plan</span>
              <span className="text-white/60 text-xs font-semibold tracking-wider uppercase">Lo que incluye</span>
              <span className="text-white/60 text-xs font-semibold tracking-wider uppercase text-right">Precio mensual</span>
            </div>

            {allMain.map((row, idx) => (
              <TableRow key={row.plan} row={row} idx={idx} total={allMain.length} />
            ))}

            {showExtra && allExtra.map((row, idx) => (
              <TableRow key={row.plan} row={row} idx={idx + allMain.length} total={allMain.length + allExtra.length} />
            ))}
          </div>
        </div>

        <div className="hidden md:flex justify-center mt-5">
          <button
            onClick={() => setShowExtra((v) => !v)}
            className="inline-flex items-center justify-center gap-2 border border-[#534AB7]/30 text-[#534AB7] text-sm font-medium px-5 py-2.5 rounded-full hover:bg-[#534AB7]/5 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className={`${showExtra ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-base`}></i>
            {showExtra ? 'Ocultar opciones adicionales' : 'Ver mas opciones'}
          </button>
        </div>

        <p className="text-[#0A0A0A]/40 text-xs mt-4 md:mt-6 text-center leading-relaxed">
          Los planes pueden incluir periodo de carencia de 90 dias desde el primer pago. Servicios sujetos a contrato, elegibilidad y condiciones del plan.
        </p>
      </div>
    </section>
  );
}
